import {
  Box,
  Center,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Link,
  Spinner,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import { categoriesToExclude } from "../../../queries/toExclude";
import { useLazyQuery } from "@apollo/client";

import NoResults from "../NoResults";
import ProductCard from "../ProductCard";
import SEARCH_QUERY from "../../../queries/searchQuery";
import { fetchAllProducts } from "../../../queries/fetchAllProducts";
import useAuth from "../../useAuth";
import { StaticImage } from "gatsby-plugin-image";
import { MdClose, MdSearch } from "../../customIcons";

const Search = () => {
  const { loggedIn, isUnlicensedUser, isCustomerDefault, isBankAcc } =
    useAuth();

  // QUERY
  const [query, setQuery] = useState("");

  const [executeSearch, { loading, data }] = useLazyQuery(SEARCH_QUERY);

  // ALL VARIABLES FOR POSTS, BRANDS, CATS...
  const products = fetchAllProducts();
  const postsData = data?.posts?.nodes;
  const brandsData = data?.tags?.nodes ?? [];
  const categoriesData = data?.categories?.nodes;
  // LOGIC FOR SHOWING SEARCH RESULTS
  const [showDropdown, setShowDropdown] = useState(false);

  // GOOGLE ANALITICS
  let timeoutId: NodeJS.Timeout;

  useEffect(() => {
    if (showDropdown) {
      clearTimeout(timeoutId);

      timeoutId = setTimeout(() => {
        window.dataLayer = window.dataLayer || [];

        window.dataLayer.push({
          event: "search",
          searchTerm: query, // Dynamic value to replace with the actual search term
        });
      }, 500);
    }

    // Cleanup the timeout on component unmount or when showDropdown becomes false
    return () => clearTimeout(timeoutId);
  }, [showDropdown, query]);

  // CLOSE COMPONENT ON CLICK OUTSIDE
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("click", handleOutsideClick);
  }, []);

  // MIN 3 CHARACTERS TO SHOW SEARCH RESULTS
  const handleInputChange = (e: any) => {
    const inputValue = e.target.value;
    if (inputValue.length >= 3) {
      setQuery(inputValue);
      executeSearch({
        variables: {
          search: inputValue,
        },
      });
      if (!showDropdown) {
        setShowDropdown(true);
      }
    } else {
      setQuery(inputValue);
      setShowDropdown(false);
    }
  };

  // BRANDS TO EXLUDE
  const filteredBrands = React.useMemo(() => {
    return brandsData?.filter((item: any) => {
      return (
        item.name !== "Unlicensed" &&
        item.name !== "Medical Devices" &&
        item.name !== "Pharmaceuticals"
      );
    });
  }, [isUnlicensedUser, isCustomerDefault, loggedIn, isBankAcc, brandsData]);

  // CATS TO EXCLUDE
  const relatedProductCategoryName = "Related Product";
  const filteredCategories = React.useMemo(() => {
    // if (isUnlicensedUser || !loggedIn || isCustomerDefault) {
    //   return categoriesData?.filter((item: any) => {
    //     return (
    //       !categoriesToExclude.includes(item.name) &&
    //       item.name !== relatedProductCategoryName
    //     );
    //   });
    // }

    return categoriesData?.filter(
      (item: any) =>
        item.name !== relatedProductCategoryName &&
        item.name !== "Recommended Products" &&
        item.name !== "Best Selling Product",
    );
  }, [isUnlicensedUser, categoriesData, categoriesToExclude]);

  // PRODUCTS TO EXCLUDE
  const filteredProducts = React.useMemo(() => {
    let allProducts = products;
    if (isUnlicensedUser || !loggedIn || isCustomerDefault || isBankAcc) {
      allProducts = products.filter((item: any) => {
        return item.productTags?.nodes.every(
          (tag: any) =>
            tag.name !== "Unlicensed" && tag.name !== "Pharmaceuticals",
        );
      });
    }

    return allProducts.filter(
      (prod: any) =>
        prod.name.toLowerCase().includes(query.toLowerCase()) ||
        prod.name.toUpperCase().includes(query),
    );
  }, [isUnlicensedUser, isCustomerDefault, isBankAcc, query]);

  // NO RESULT/RESULTS FOR SPECIFIC SEARCH

  // NO RESULTS FOR ALL
  const isNoResults =
    filteredProducts?.length === 0 &&
    filteredBrands?.length === 0 &&
    filteredCategories?.length === 0 &&
    (postsData ? postsData.length === 0 : true);

  return (
    <Stack w="100%" flexDir="row" alignItems="center" h="64px" zIndex={1000}>
      <form autoComplete="off" style={{ width: "100%" }}>
        <InputGroup>
          <InputLeftElement
            pointerEvents="none"
            color="gray.400"
            fontSize="lg"
            borderRadius="4px"
          >
            <MdSearch fill="#CB809E" />
          </InputLeftElement>

          <Input
            onFocus={(event) => {
              event.target.setAttribute("autocomplete", "new-password");
            }}
            name="search"
            autoComplete="off"
            ref={inputRef}
            placeholder="Search Medical Products and Supplies"
            rounded="md"
            textColor="#4A5568"
            borderColor="#A0AEC0"
            focusBorderColor="#BB5D82"
            width="100%"
            bg="#fff"
            onChange={handleInputChange}
            value={query}
          />

          {showDropdown && (
            <Stack
              py="24px"
              ref={dropdownRef}
              position="fixed"
              left={0}
              top="100%" // Position the dropdown below the input
              bg="#fff"
              width="100vw" // Full width of the viewport
              height="100vh" // Adjust as needed
              boxShadow="0 2px 4px rgba(0, 0, 0, 0.1)"
            >
              {loading ? (
                <Center h="100vh">
                  {" "}
                  <Spinner alignItems="center" justifyContent="center" />
                </Center>
              ) : (
                <>
                  {" "}
                  {isNoResults ? (
                    <>
                      <Center pt="100px" id="no-search-results">
                        <Box
                          minW="280px"
                          width={{ "2xl": "575px" }}
                          alignItems="center"
                          mx="auto"
                        >
                          <Center>
                            {" "}
                            <StaticImage
                              rel="preload"
                              src="../../../images/noSearchResults.webp"
                              style={{
                                height: "240px",
                                width: "450px",
                                backgroundColor: "transparent !important",
                              }}
                              alt="search"
                            />
                          </Center>

                          <Text
                            textAlign="center"
                            color="#171923"
                            fontSize="20px"
                            fontWeight="600"
                          >
                            Sorry, there are no products, brands or articles
                            that match your search.
                          </Text>
                          <Text
                            color="#4A5568"
                            pt="8px"
                            textAlign="center"
                            fontSize="18px"
                          >
                            Try changing your search term or send us an inquiry
                            via the{" "}
                            <Link
                              href="/contact/"
                              color="#BB5D82"
                              textDecoration="underline"
                            >
                              contact form
                            </Link>
                            .
                          </Text>
                        </Box>
                      </Center>
                    </>
                  ) : (
                    <>
                      <Stack h="90%" overflowY="scroll" px="40px">
                        <IconButton
                          onClick={() => {
                            setShowDropdown(!showDropdown);
                            setQuery("");
                          }}
                          position="absolute"
                          right={4}
                          width="24px"
                          height="24px"
                          bg="fff"
                          aria-label=""
                          icon={<MdClose />}
                        />

                        <HStack
                          // maxW="1456px"
                          justifyContent="space-between"
                          spacing={20}
                          // px={{ lg: "40px", xl: "80px" }}
                          // mx="auto"
                          alignItems="flex-start"
                        >
                          <VStack
                            maxW="552px"
                            zIndex={1000}
                            mb={{ base: "48px", "2xl": "24px" }}
                          >
                            {/* PRODUCTS */}
                            <NoResults
                              items={filteredProducts}
                              title="Products"
                              columns={{ lg: 1, xl: 2 }}
                              mapFunction={(item: {
                                id: string;
                                slug: string;
                                name: string;
                                description: string;
                                databaseId: number;
                                featuredImage: any;
                                allPaActiveIngredient: any;
                                stockStatus: any;
                                isOnPromotion: any;
                                altText: string;
                              }) => {
                                const imageData =
                                  item.featuredImage?.node?.localFile
                                    ?.childImageSharp?.gatsbyImageData;
                                const altText =
                                  item.featuredImage?.node?.altText;
                                return (
                                  <Link
                                    style={{ textDecoration: "none" }}
                                    href={`/product/${item.slug}/`}
                                    key={item.id}
                                  >
                                    <ProductCard
                                      heading="div"
                                      border="1px solid #E1E7EF"
                                      name={item.name}
                                      description={item.description}
                                      // activeSubstance={
                                      //   item.allPaActiveIngredient?.nodes
                                      // }
                                      key={item.id}
                                      productId={item.databaseId}
                                      imageData={imageData}
                                      altText={altText}
                                      slug={item.slug}
                                      isSearch
                                      isProductOnPromotion={
                                        item?.isOnPromotion?.isonpromotion
                                      }
                                    />
                                  </Link>
                                );
                              }}
                            />
                          </VStack>

                          <VStack w="33%" alignItems="flex-start" gap="24px">
                            {/* BRANDS exist */}
                            <NoResults
                              items={filteredBrands}
                              title="Brands"
                              mapFunction={(item: {
                                name: string;
                                slug: string;
                              }) => (
                                <Link
                                  key={item.slug}
                                  href={`/${item.slug}/`}
                                  fontSize="20px"
                                  fontWeight="500"
                                  color="#1A212D"
                                >
                                  {item.name.toUpperCase()}
                                </Link>
                              )}
                            />

                            {/* CATEGORIES exist */}
                            <NoResults
                              items={filteredCategories}
                              title="Categories"
                              mapFunction={(item: {
                                name: string;
                                slug: string;
                              }) => (
                                <Link
                                  key={item.slug}
                                  href={`/${item.slug}/`}
                                  fontSize="20px"
                                  fontWeight="500"
                                  color="#1A212D"
                                >
                                  {item.name}
                                </Link>
                              )}
                            />
                          </VStack>

                          <VStack maxW="412px" pr="20px">
                            {/* POSTS exist  */}
                            <NoResults
                              items={postsData}
                              title="Articles"
                              mapFunction={(item: {
                                title: string;
                                slug: string;
                                excerpt: string;
                                categories: { nodes: { slug: string }[] };
                              }) => (
                                <Box pb="12px">
                                  <Link
                                    href={`/blog/${item.categories.nodes[0].slug}/${item.slug}/`}
                                    fontSize="20px !important"
                                    fontWeight="500 !important"
                                    lineHeight="28px"
                                    color="#171923"
                                  >
                                    <Box
                                      pb="12px"
                                      dangerouslySetInnerHTML={{
                                        __html: String(item.title),
                                      }}
                                    />
                                  </Link>
                                  <Box
                                    fontWeight="400 !important"
                                    color="#4A5568 !important"
                                    fontSize="14px !important"
                                    lineHeight="19.6px"
                                    noOfLines={3}
                                    dangerouslySetInnerHTML={{
                                      __html: String(item.excerpt),
                                    }}
                                  />
                                </Box>
                              )}
                            />
                          </VStack>
                        </HStack>
                      </Stack>
                    </>
                  )}
                </>
              )}
            </Stack>
          )}
        </InputGroup>
      </form>
    </Stack>
  );
};

export default Search;
